import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { FIELD_LISTS_STALE_MS, invalidateFieldLists } from "@/hooks/useWorkflowData";
import { apiFetch } from "@/lib/api-client";
import type {
  AiProvidersResponse,
  AiProviderName,
  AiVerifyResult,
  AllAdsResponse,
  CatalogsResponse,
  CoexistenceSyncBody,
  CommerceSettings,
  CommerceAccountsResponse,
  ConnectionResponse,
  CustomFieldDefinition,
  CustomFieldDraft,
  CustomFieldValueType,
  CustomFieldsResponse,
  FacebookCardKey,
  FieldCatalogObjectType,
  IgAccountSettingsPatch,
  IgAccountsResponse,
  LeadFormsResponse,
  WhatsAppHealth,
  LinkDiagnostics,
  ProductCreateBody,
  ProductsResponse,
  ProductUpdateBody,
  ProductWriteResult,
  PublicConfig,
  RegisterNumberBody,
  RemovableAssetType,
  WhatsAppConnectBody,
  WhatsAppNumberActionResult,
  WhatsAppNumbersStatusResponse,
} from "@/types/meta";

export const adsConnectionKey: QueryKey = ["meta", "connection", "ads"];
export const waConnectionKey: QueryKey = ["meta", "connection", "whatsapp"];
export const publicConfigKey: QueryKey = ["meta", "public-config"];

/** Non-secret; served unauthenticated. Never changes during a session. */
export function usePublicConfigQuery() {
  return useQuery({
    queryKey: publicConfigKey,
    queryFn: () => apiFetch<PublicConfig>("/api/public-config"),
    staleTime: Infinity,
  });
}

export function useAdsConnectionQuery() {
  return useQuery({
    queryKey: adsConnectionKey,
    queryFn: () => apiFetch<ConnectionResponse>("/api/meta/ads/connection"),
  });
}

export function useWhatsAppConnectionQuery() {
  return useQuery({
    queryKey: waConnectionKey,
    queryFn: () => apiFetch<ConnectionResponse>("/api/meta/whatsapp/connection"),
  });
}

export interface ConnectAdsVariables {
  code: string;
  /** The Facebook card whose window produced the code. Omitted = the all-in-one login. */
  card?: FacebookCardKey;
}

export function useConnectAdsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, card }: ConnectAdsVariables) =>
      apiFetch<ConnectionResponse>("/api/meta/ads/connect", {
        method: "POST",
        body: card ? { code, card } : { code },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adsConnectionKey });
      void queryClient.invalidateQueries({ queryKey: ["meta", "diagnostics"] });
      void queryClient.invalidateQueries({ queryKey: ["meta", "catalogs"] });
    },
  });
}

/** Every screen that reads the Facebook connection's assets, refreshed after a card changes
 *  them: the reports, the link check, the catalog pickers and the Instagram settings. */
function refreshFacebookReaders(queryClient: ReturnType<typeof useQueryClient>) {
  for (const queryKey of [
    ["meta", "diagnostics"],
    ["meta", "all-ads"],
    ["meta", "catalogs"],
    ["meta", "instagram", "accounts"],
  ]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

/** Remove one ad account, Page, Instagram account or catalog from ORAI. It stays granted at
 *  Meta; ORAI stops storing and reading it. The response is the new card state. */
export function useRemoveMetaAssetMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (asset: { asset_type: RemovableAssetType; asset_id: string }) =>
      apiFetch<ConnectionResponse>("/api/meta/ads/assets/remove", { method: "POST", body: asset }),
    onSuccess: (data) => {
      queryClient.setQueryData(adsConnectionKey, data);
      refreshFacebookReaders(queryClient);
    },
  });
}

/** Undo a removal; the server reads the asset back from Meta before answering. */
export function useRestoreMetaAssetMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (asset: { asset_type: RemovableAssetType; asset_id: string }) =>
      apiFetch<ConnectionResponse>("/api/meta/ads/assets/restore", { method: "POST", body: asset }),
    onSuccess: (data) => {
      queryClient.setQueryData(adsConnectionKey, data);
      refreshFacebookReaders(queryClient);
    },
  });
}

/** Revoke one card: its own permissions at Meta and its assets in ORAI. Meta's refusals come
 *  back in `warnings`, never as an error, so the card can say what is still granted. */
export function useRevokeFacebookCardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (card: FacebookCardKey) =>
      apiFetch<ConnectionResponse>(`/api/meta/ads/cards/${card}`, { method: "DELETE" }),
    onSuccess: (data) => {
      queryClient.setQueryData(adsConnectionKey, data);
      refreshFacebookReaders(queryClient);
    },
  });
}

export function useConnectWhatsAppMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WhatsAppConnectBody) =>
      apiFetch<ConnectionResponse>("/api/meta/whatsapp/connect", { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: waConnectionKey });
      void queryClient.invalidateQueries({ queryKey: ["meta", "diagnostics"] });
      // A connect sends no PIN, so a new number arrives PENDING and its row offers
      // "Register number" -- but only once this live status is read again.
      void queryClient.invalidateQueries({ queryKey: waNumbersStatusKey });
    },
  });
}

export function useDisconnectMutation(product: "ads" | "whatsapp") {
  const queryClient = useQueryClient();
  const key = product === "ads" ? adsConnectionKey : waConnectionKey;
  return useMutation({
    mutationFn: () => apiFetch<{ status?: string }>(`/api/meta/${product}/connection`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ["meta", "diagnostics"] });
    },
  });
}

/** Re-reads assets from Graph for an already-connected ad account. */
export function useSyncAdsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<ConnectionResponse>("/api/meta/ads/sync", { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adsConnectionKey });
    },
  });
}

/** Always HTTP 200 -- rate limits and expired tokens degrade into `warnings` so the
 *  card still renders a verdict. A 60s server TTL means a just-fixed link shows up on
 *  the next click, so this is deliberately not cached hard on the client. */
export function useDiagnosticsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["meta", "diagnostics"],
    queryFn: () => apiFetch<LinkDiagnostics>("/api/meta/ads/diagnostics"),
    enabled,
    staleTime: 30_000,
  });
}

export function useAllAdsQuery(params: {
  status: string;
  search: string;
  whatsappOnly: boolean;
  since?: string | null;
  until?: string | null;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params.status !== "ALL") query.set("status", params.status);
  if (params.search.trim() !== "") query.set("search", params.search.trim());
  if (params.whatsappOnly) query.set("whatsapp_only", "true");
  if (params.since) query.set("since", params.since);
  if (params.until) query.set("until", params.until);
  if (params.limit != null) query.set("limit", String(params.limit));
  if (params.offset) query.set("offset", String(params.offset));
  const qs = query.toString();

  return useQuery({
    queryKey: [
      "meta",
      "all-ads",
      params.status,
      params.search.trim(),
      params.whatsappOnly,
      params.since,
      params.until,
      params.limit,
      params.offset,
    ],
    queryFn: () => apiFetch<AllAdsResponse>(`/api/dashboard/all-ads${qs ? `?${qs}` : ""}`),
    placeholderData: (previous) => previous,
  });
}

export function useCatalogsQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["meta", "catalogs"],
    queryFn: () => apiFetch<CatalogsResponse>("/api/meta/catalog/catalogs"),
    // Off until a caller needs it: this endpoint scans Meta and refreshes the stored assets.
    enabled: options.enabled ?? true,
  });
}

export function useCatalogProductsQuery(catalogId: string | null) {
  return useQuery({
    queryKey: ["meta", "catalog-products", catalogId],
    queryFn: () => apiFetch<ProductsResponse>(`/api/meta/catalog/${catalogId}/products`),
    enabled: catalogId != null,
  });
}

export function useCommerceAccountsQuery() {
  return useQuery({
    queryKey: ["meta", "commerce-accounts"],
    queryFn: () => apiFetch<CommerceAccountsResponse>("/api/meta/commerce/accounts"),
  });
}

export function useLeadFormsQuery(pageId: string | null) {
  return useQuery({
    queryKey: ["meta", "lead-forms", pageId],
    queryFn: () => apiFetch<LeadFormsResponse>(`/api/meta/pages/${pageId}/lead-forms`),
    enabled: pageId != null,
  });
}

/** Takes the wider catalog type: "whatsapp" lists the fields WhatsApp itself fills (ad click,
 *  latest form response) as system attributes, and never has custom ones. */
export function useCustomFieldsQuery(objectType: FieldCatalogObjectType) {
  return useQuery({
    queryKey: ["custom-fields", objectType],
    queryFn: () => apiFetch<CustomFieldsResponse>(`/api/custom-fields?object_type=${objectType}`),
    // Also the "Save to contact" list on Ask a question and the Form step
    // (useFillableContactFields): as fresh as the canvas's other field lists, for a field just
    // made in another tab.
    staleTime: FIELD_LISTS_STALE_MS,
  });
}

// Definitions are written through /api/agent/attributes, not /api/custom-fields: one
// table (agent_attribute_defs) backs both screens, and the agent router already owns
// create/update/delete for it. A parallel write path would be a second validator to
// keep in step with clean_definition.
//
// Every one of these refreshes EVERY list of fields a page may hold: both object types on this
// screen, the importer's own field list, the canvas's definitions and `{field}` catalog
// (invalidateFieldLists), and the Smart Tags builder's. A definition is one row in one table;
// caching it per screen and refreshing only the screen you were on is how a field ends up visible
// in one place and missing in another -- a new field reached a Condition minutes late this way.
function invalidateFieldCaches(queryClient: ReturnType<typeof useQueryClient>) {
  invalidateFieldLists(queryClient);
  // useSmartTagAttributesQuery, one per pipeline, is cached forever (staleTime Infinity), so
  // nothing but an invalidation ever brings it a new field. It lists OPPORTUNITY fields only,
  // which is why the automation side (invalidateFieldLists), making contact fields, leaves it.
  void queryClient.invalidateQueries({
    queryKey: ["smart-tags"],
    predicate: (query) => query.queryKey[2] === "attributes",
  });
}

/** Drafts a field's extraction instruction — `Task.DESCRIBE`, one cheap model call.
 *
 *  Not a query and it invalidates nothing: the endpoint WRITES nothing, it hands back a
 *  sentence the operator can edit or discard before saving the field. It sits behind
 *  `require_enabled` (the rest of that router is data entry that must work with no model
 *  key; this one spends), so a workspace with no key gets a 4xx whose `detail` is the
 *  message to show. Repeats are free — the service fingerprints on the same inputs, so an
 *  operator clicking twice is not billed twice.
 */
export function useDescribeFieldMutation() {
  return useMutation({
    mutationFn: (input: {
      label: string;
      value_type: CustomFieldValueType;
      options?: string[];
      hint?: string;
    }) =>
      apiFetch<{ description: string }>("/api/agent/attributes/describe", {
        method: "POST",
        body: input,
      }),
  });
}

export function useCreateCustomFieldMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: CustomFieldDraft) =>
      apiFetch<CustomFieldDefinition>("/api/agent/attributes", { method: "POST", body: draft }),
    onSuccess: () => invalidateFieldCaches(queryClient),
  });
}

export function useUpdateCustomFieldMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    // PATCH, not PUT: the server treats a partial payload as a partial update, and the
    // `key` is deliberately immutable once created (renaming it would orphan every value
    // already stored under it -- see agent_attributes.update).
    mutationFn: ({ id, patch }: { id: number; patch: Partial<CustomFieldDraft> }) =>
      apiFetch<CustomFieldDefinition>(`/api/agent/attributes/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => invalidateFieldCaches(queryClient),
  });
}

export function useDeleteCustomFieldMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ status: string }>(`/api/agent/attributes/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateFieldCaches(queryClient),
  });
}


// ── Instagram account switches ──

export const igAccountsKey: QueryKey = ["meta", "instagram", "accounts"];

/** Pure DB read server-side -- no Graph call, so it is safe to fire whenever the modal
 *  opens. `enabled` gates it on the ads connection, because IG accounts only exist as
 *  assets on that connection and an unconnected tenant would fetch an empty list. */
export function useIgAccountsQuery(enabled: boolean) {
  return useQuery({
    queryKey: igAccountsKey,
    queryFn: () => apiFetch<IgAccountsResponse>("/api/meta/instagram/accounts"),
    enabled,
  });
}

export function useIgAccountSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ igId, patch }: { igId: string; patch: IgAccountSettingsPatch }) =>
      apiFetch<IgAccountsResponse["accounts"][number]>(
        `/api/meta/instagram/${igId}/settings`,
        { method: "PATCH", body: patch },
      ),
    // Writes the server's answer straight into the cache instead of refetching. The
    // server can legitimately return something the click did not ask for -- switching
    // messaging off disarms automation with it -- so the row must land on what was
    // SAVED, and a refetch would show the old value for one paint on the way there.
    onSuccess: (row) => {
      queryClient.setQueryData<IgAccountsResponse>(igAccountsKey, (prev) =>
        prev == null
          ? prev
          : { accounts: prev.accounts.map((a) => (a.ig_id === row.ig_id ? row : a)) },
      );
    },
  });
}

// ── AI providers ──
// Every one of these lives behind /api/ai/*, which 503s when AI is switched off for the
// deployment. `aiEnabled` from /api/public-config is the gate -- it is reachable whether
// or not AI is on, which is the whole reason it exists.

export const aiProvidersKey: QueryKey = ["ai", "providers"];

export function useAiProvidersQuery(enabled: boolean) {
  return useQuery({
    queryKey: aiProvidersKey,
    queryFn: () => apiFetch<AiProvidersResponse>("/api/ai/providers"),
    enabled,
  });
}

export function useSaveAiProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      apiKey,
      config,
    }: {
      provider: AiProviderName;
      apiKey: string;
      /** Vector providers only: the index host. Omitted -- and ignored -- otherwise. */
      config?: string;
    }) =>
      apiFetch<AiProvidersResponse>(`/api/ai/providers/${provider}`, {
        method: "PUT",
        body: config != null ? { api_key: apiKey, config } : { api_key: apiKey },
      }),
    // All three provider mutations return the FULL provider list, so the cache is set
    // from the response rather than invalidated -- one round trip, not two.
    onSuccess: (data) => queryClient.setQueryData(aiProvidersKey, data),
  });
}

export function useDeleteAiProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: AiProviderName) =>
      apiFetch<AiProvidersResponse>(`/api/ai/providers/${provider}`, { method: "DELETE" }),
    onSuccess: (data) => queryClient.setQueryData(aiProvidersKey, data),
  });
}

/** Spends a fraction of a cent on a real vendor call. A wrong key comes back as HTTP 200
 *  with `verified: false`, so the result is rendered, not thrown. */
export function useVerifyAiProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: AiProviderName) =>
      apiFetch<AiVerifyResult>(`/api/ai/providers/${provider}/verify`, { method: "POST" }),
    // Verify persists verified_at / last_error server-side but answers with only the
    // verdict, so the list genuinely has to be re-read here.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiProvidersKey });
    },
  });
}


// ── WhatsApp health + commerce settings ──

export const waHealthKey: QueryKey = ["meta", "whatsapp", "health"];
export const commerceSettingsKey: QueryKey = ["meta", "whatsapp", "commerce-settings"];

/** Meta's own send-capability and WABA-linked-catalog signal. Deliberately separate from
 *  the heavy ad-account diagnostic so the badge renders on load, not on a button press.
 *  Also the ONLY source of `linked_catalogs`, which is what marks a catalog chip as
 *  actually usable in a WhatsApp send. */
export function useWhatsAppHealthQuery(enabled: boolean) {
  return useQuery({
    queryKey: waHealthKey,
    queryFn: () => apiFetch<WhatsAppHealth>("/api/meta/whatsapp/health"),
    enabled,
    // The notice bar mounts this on EVERY page, and each read is three Graph calls against a
    // WhatsApp Business account Meta allows 200 an hour. At 60s and a refetch on every tab
    // focus, page loads alone spent that on 2026-09-25 (#80008). The backend reuses a verdict
    // for ten minutes too, so asking more often could only ever return the same answer.
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useCommerceSettingsQuery(enabled: boolean) {
  return useQuery({
    queryKey: commerceSettingsKey,
    queryFn: () => apiFetch<CommerceSettings>("/api/meta/whatsapp/commerce-settings"),
    enabled,
  });
}

/** Flips the storefront icon. The POST re-reads Meta and returns the settled state, so
 *  the response is written straight to the cache -- Meta is the authority on whether the
 *  flip actually took, and echoing the optimistic value would hide a rejected write. */
export function useToggleCatalogVisibilityMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isCatalogVisible: boolean) =>
      apiFetch<CommerceSettings>("/api/meta/whatsapp/commerce-settings", {
        method: "POST",
        body: { is_catalog_visible: isCatalogVisible },
      }),
    onSuccess: (data) => queryClient.setQueryData(commerceSettingsKey, data),
  });
}

/** Scans Meta for catalogs. The scan WRITES `catalog` asset rows server-side, so the
 *  chips are refreshed by re-reading the ads connection afterwards rather than from this
 *  response -- the endpoint's own payload is only the scan's outcome and its warnings. */
export function useRefreshCatalogsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<CatalogsResponse>("/api/meta/catalog/catalogs"),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adsConnectionKey });
      void queryClient.invalidateQueries({ queryKey: ["meta", "catalogs"] });
    },
  });
}


// ── Catalog product writes ──
// Both invalidate the catalog LIST as well as the product list: creating a product moves
// `product_count`, and a sidebar still showing the old count next to a grid showing the
// new one is the kind of disagreement that makes an operator submit twice.

function invalidateCatalog(queryClient: ReturnType<typeof useQueryClient>, catalogId: string) {
  void queryClient.invalidateQueries({ queryKey: ["meta", "catalog-products", catalogId] });
  void queryClient.invalidateQueries({ queryKey: ["meta", "catalogs"] });
}

export function useCreateProductMutation(catalogId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    // Throws rather than posting to a path with an empty segment: the UI already blocks
    // this, and a write path should fail loudly instead of asking Meta what it thinks
    // "/catalog//products" means.
    mutationFn: (body: ProductCreateBody) => {
      if (catalogId == null) throw new Error("Choose a catalog first.");
      return apiFetch<ProductWriteResult>(
        `/api/meta/catalog/${encodeURIComponent(catalogId)}/products`,
        { method: "POST", body },
      );
    },
    onSuccess: () => {
      if (catalogId != null) invalidateCatalog(queryClient, catalogId);
    },
  });
}

/** Update is a POST, not a PATCH -- that is Meta's own shape for a product write, and the
 *  backend mirrors it rather than inventing a REST verb the Graph call does not have. */
export function useUpdateProductMutation(catalogId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: ProductUpdateBody }) => {
      if (catalogId == null) throw new Error("Choose a catalog first.");
      return apiFetch<ProductWriteResult>(
        `/api/meta/catalog/${encodeURIComponent(catalogId)}/products/${encodeURIComponent(productId)}`,
        { method: "POST", body },
      );
    },
    onSuccess: () => {
      if (catalogId != null) invalidateCatalog(queryClient, catalogId);
    },
  });
}


// ── WhatsApp numbers: live status, registration, coexistence import ──

export const waNumbersStatusKey: QueryKey = ["meta", "whatsapp", "numbers-status"];

/** One LIVE Graph read per number server-side, so it is gated on the connection and given
 *  the health badge's staleness rather than being refetched on every focus. It exists to
 *  answer one question the stored connection cannot: has this number actually been
 *  registered yet, or is Meta still reporting it PENDING. */
export function useWhatsAppNumbersStatusQuery(enabled: boolean) {
  return useQuery({
    queryKey: waNumbersStatusKey,
    queryFn: () => apiFetch<WhatsAppNumbersStatusResponse>("/api/meta/whatsapp/numbers/status"),
    enabled,
    staleTime: 60_000,
  });
}

/** Completes a Cloud API number by registering it with a 6-digit two-step PIN. Never for a
 *  coexistence number -- Meta rejects that, and the server 409s before Graph is called.
 *  Both the stored connection and the live status move, so both are re-read. */
export function useRegisterWhatsAppNumberMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ phoneNumberId, pin }: { phoneNumberId: string } & RegisterNumberBody) =>
      apiFetch<WhatsAppNumberActionResult>(
        `/api/meta/whatsapp/numbers/${encodeURIComponent(phoneNumberId)}/register`,
        { method: "POST", body: { pin } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: waNumbersStatusKey });
      void queryClient.invalidateQueries({ queryKey: waConnectionKey });
      void queryClient.invalidateQueries({ queryKey: waHealthKey });
    },
  });
}

/** Re-asks Meta for whichever of the two coexistence imports has no request id yet. The
 *  outcome is written onto the phone_number asset's `extra.coexistence`, so the connection
 *  query is what redraws the panel -- this response carries only the acknowledgement. */
export function useCoexistenceSyncMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CoexistenceSyncBody) =>
      apiFetch<WhatsAppNumberActionResult>("/api/meta/whatsapp/coexistence/sync", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: waConnectionKey });
    },
  });
}
